import { useInstantQuery } from '@repo/toolkit/use-instant-query'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'

import { getAffectedStaffDailyReport } from '../../../api/affected-staffs/get-affected-daily-report'
import { getAffectedStaffActions } from '../../../api/affected-staffs/get-affected-staff-actions'
import { saveAffectedStaffDailyReport } from '../../../api/affected-staffs/save-affected-staff-daily-report'
import { submitAffectedStaffDailyReport } from '../../../api/affected-staffs/submit-affected-staff-daily-report'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import AffectedStaffDailyReportForm from '../../../components/pages/affected-staffs/affected-staff-daily-report-form'
import AffectedStaffDailyReportModal from '../../../components/pages/affected-staffs/affected-staff-daily-report-modal'
import {
  affectedStaffDailyReportInitialValues,
  mapAffectedStaffDailyReportToInput
} from '../../../components/pages/affected-staffs/utils'
import { Paths } from '../../../lib/constants/path'
import useAffectedStaff from '../../../lib/hooks/use-affected-staff'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffDailyReportInput } from '../../../lib/types/models/affectedStaff/affected-staff-daily-report'

export default function StaffPersonalSpaceJourneyDetailsDailyReport() {
  const { t } = useTranslate()
  const { affectedStaff, isLoading } = useAffectedStaff()
  const { selectedData, modalState, toggleModal } = useModalManager<
    'affected-staff-daily-report',
    AffectedStaffDailyReportInput
  >(['affected-staff-daily-report'])

  const { data: affectedStaffActions, isPending: isActionsPending } = useQuery({
    queryKey: ['affected-staff-actions', affectedStaff?.id],
    queryFn: () => {
      if (affectedStaff?.id) return getAffectedStaffActions(affectedStaff.id)
    },
    enabled: !!affectedStaff?.id
  })

  const report = useInstantQuery({
    queryFn: () => {
      if (!affectedStaff) throw new Error('No affected staff')
      return getAffectedStaffDailyReport(affectedStaff.id)
    },
    enabled: !!affectedStaff && !!affectedStaffActions?.canAccessDailyReport
  })

  const saveForLater = useMutation({
    mutationFn: saveAffectedStaffDailyReport,
    onSuccess() {
      toast.success('Rapport sauvegardé avec succès')
    }
  })
  const submit = useMutation({
    mutationFn: submitAffectedStaffDailyReport,
    onSuccess() {
      toast.success('Rapport soumis avec succès')
      report.refetch()
      toggleModal('affected-staff-daily-report')
    }
  })

  if (isLoading || isActionsPending) return <LoadingScreen />
  if (!affectedStaff) return <Typography variant="text">Affected staff not found</Typography>
  if (!affectedStaffActions?.canAccessDailyReport)
    return <Navigate to={`../${Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_HOME}`} replace />
  if (report.isPending) return <LoadingScreen />

  return (
    <div className={'flex flex-col gap-4'}>
      <Typography variant="h3">
        {t('staff.personal.space.my.journeys.details.daily.report')}
      </Typography>

      <AffectedStaffDailyReportForm
        defaultValues={
          report.data
            ? mapAffectedStaffDailyReportToInput(report.data)
            : affectedStaffDailyReportInitialValues
        }
        handleSubmit={async (data, type) => {
          if (type === 'save-for-later')
            await saveForLater.mutateAsync({ input: data, affectedStaffId: affectedStaff.id })
          else toggleModal('affected-staff-daily-report', data)
        }}
      />

      {selectedData && (
        <AffectedStaffDailyReportModal
          isOpen={modalState['affected-staff-daily-report']}
          onClose={() => toggleModal('affected-staff-daily-report')}
          input={selectedData}
          isLoading={submit.isPending}
          onConfirm={async () => {
            if (selectedData)
              await submit.mutateAsync({ input: selectedData, affectedStaffId: affectedStaff.id })
          }}
        />
      )}
    </div>
  )
}
